import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import {
  CalendarHeart, CheckCircle2, XCircle, Coins, Heart, Cat,
  MapPin, Sparkles, PartyPopper, Users, Pencil, Star, Camera, Share2, Images, Award, Download, Instagram, MessageCircle,
} from "lucide-react";
import { useApp } from "../context/AppState";
import { getDestination } from "../data/destinations";
import { SEASONS, SEASON_ORDER } from "../data/season";
import { rupiah } from "../lib/format";
import Modal from "../components/Modal";
import MemoryShareCard from "../components/MemoryShareCard";

const TABS = [
  { id: "confirmed", label: "Akan datang" },
  { id: "completed", label: "Selesai" },
  { id: "cancelled", label: "Dibatalkan" },
];

function daysTogether(anniv) {
  if (!anniv) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(anniv).getTime()) / 86400000));
}

export default function Cerita() {
  const navigate = useNavigate();
  const { state, completeBooking, cancelBooking, setActivePerson, toggleSaved, updateProfile, addMemory, anniversaryToday } = useApp();
  const [tab, setTab] = useState("confirmed");
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ personA: "", personB: "", anniversary: "" });
  const [memoryFor, setMemoryFor] = useState(null);
  const [mem, setMem] = useState({ note: "", rating: 5, photo: "" });
  const [cardFor, setCardFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const cardRef = useRef(null);

  if (!state) return null;

  const bookings = state.bookings || [];
  const filtered = bookings.filter((b) => b.status === tab);
  const counts = {
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    completed: bookings.filter((b) => b.status === "completed").length,
    cancelled: bookings.filter((b) => b.status === "cancelled").length,
  };
  const savedDests = (state.saved || []).map(getDestination).filter(Boolean);
  const people = [state.personA, state.personB].filter(Boolean);
  const memories = bookings.filter((b) => b.memory && (b.memory.photo || b.memory.note));

  const shareAlbum = async () => {
    const lines = memories
      .map((b) => `• ${b.destName} ${"★".repeat(b.memory.rating || 0)}${b.memory.note ? " — " + b.memory.note : ""}`)
      .join("\n");
    const text = `Album kencan ${state.coupleName} di KencanKita 💕\n\n${lines}\n\n${memories.length} kenangan tersimpan.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Album Kencan Kami", text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Teks album disalin ke clipboard 📋");
      }
    } catch {}
  };

  const openEdit = () => {
    setForm({ personA: state.personA, personB: state.personB, anniversary: state.anniversary || "" });
    setEditOpen(true);
  };
  const saveEdit = () => {
    updateProfile({
      personA: form.personA || state.personA,
      personB: form.personB || state.personB,
      anniversary: form.anniversary,
      coupleName: `${form.personA || state.personA} & ${form.personB || state.personB}`,
    });
    setEditOpen(false);
    toast.success("Profil diperbarui");
  };

  const complete = (id) => {
    const b = bookings.find((x) => x.id === id);
    completeBooking(id);
    toast.success(`Kencan selesai! +${Math.floor(b.total / 1000)} poin ✨`);
  };
  const cancel = (id) => { cancelBooking(id); toast("Reservasi dibatalkan"); };

  const openMemory = (b) => {
    setMem(b.memory || { note: "", rating: 5, photo: "" });
    setMemoryFor(b.id);
  };
  const saveMemory = () => {
    addMemory(memoryFor, mem);
    setMemoryFor(null);
    toast.success("Kenangan tersimpan 💕");
  };

  const genBlob = async () => {
    const node = cardRef.current;
    if (!node) return null;
    let dataUrl;
    try {
      dataUrl = await toPng(node, { pixelRatio: 2, cacheBust: true });
    } catch {
      dataUrl = await toPng(node, { pixelRatio: 2, cacheBust: true, skipFonts: true });
    }
    const res = await fetch(dataUrl);
    return await res.blob();
  };

  const cardFilename = () => `kencankita-${(cardFor?.destName || "kenangan").toLowerCase().replace(/\s+/g, "-")}.png`;

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setSaving(true);
    try {
      const blob = await genBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.download = cardFilename();
      a.href = url;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Kartu kenangan diunduh 📸");
    } catch {
      toast.error("Gagal membuat kartu. Coba lagi.");
    } finally {
      setSaving(false);
    }
  };

  const shareCard = async (channel) => {
    if (!cardRef.current) return;
    setSaving(true);
    try {
      const blob = await genBlob();
      const file = new File([blob], cardFilename(), { type: "image/png" });
      const text = `Kenangan kencan ${state.coupleName} di ${cardFor?.destName} 💕 #KencanKita`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: "Kartu Kenangan KencanKita" });
        return;
      }
      // Fallback: unduh gambar lalu buka kanal
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.download = cardFilename();
      a.href = url;
      a.click();
      URL.revokeObjectURL(url);
      if (channel === "whatsapp") {
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
        toast.success("Gambar diunduh — lampirkan ke WhatsApp 💬");
      } else if (channel === "instagram") {
        toast.success("Gambar diunduh — buka Instagram Story lalu unggah 📸");
      } else {
        toast.success("Gambar diunduh 📸");
      }
    } catch {
      toast.error("Gagal membagikan. Coba lagi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="kk-reveal">
      {anniversaryToday && (
        <div className="mb-5 flex items-center gap-3 overflow-hidden rounded-3xl kk-coral-gradient p-5 text-white" data-testid="anniversary-banner">
          <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-white/20"><PartyPopper className="h-6 w-6" /></span>
          <div className="flex-1">
            <div className="text-[11px] font-bold uppercase tracking-widest text-white/85">Hari jadian kalian!</div>
            <div className="text-[17px] font-extrabold">Selamat hari jadian, {state.coupleName}! 🎉</div>
            <p className="text-[13px] text-white/85">Mochi ikut merayakan — kalian dapat bonus +200 poin hari ini.</p>
          </div>
        </div>
      )}
      {/* header */}
      <section className="overflow-hidden rounded-3xl border border-[#efeae4] bg-white">
        <div className="h-24 kk-coral-gradient sm:h-28" />
        <div className="px-5 pb-5 sm:px-7">
          <div className="-mt-10 flex items-end gap-4">
            <div className="flex">
              {people.map((p, i) => (
                <span key={i} className={`grid h-20 w-20 place-items-center rounded-full border-4 border-white text-xl font-extrabold ${i ? "-ml-6 bg-[#ffd0cb] text-[#c9566b]" : "bg-[#ffe1dd] text-[#c9566b]"}`}>
                  {p?.[0] || "?"}
                </span>
              ))}
            </div>
            <button onClick={openEdit} data-testid="edit-profile"
              className="mb-2 ml-auto flex items-center gap-1.5 rounded-full border border-[#efeae4] bg-white px-3 py-1.5 text-[12px] font-bold text-[#7c7277]">
              <Pencil className="h-3.5 w-3.5" /> Ubah
            </button>
          </div>
          <h1 className="mt-3 text-2xl font-extrabold tracking-tight">{state.coupleName}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#8a8278]">
            <span className="flex items-center gap-1"><CalendarHeart className="h-4 w-4 text-[#ff5a5f]" /> {daysTogether(state.anniversary)} hari bersama</span>
            <span className="flex items-center gap-1"><Coins className="h-4 w-4 text-[#e0a94b]" /> {Number(state.points).toLocaleString("id-ID")} poin</span>
            <span className="flex items-center gap-1"><Cat className="h-4 w-4 text-[#c9878f]" /> Mochi level {state.mochi.level}</span>
          </div>

          {/* active caretaker */}
          <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl bg-[#f5f1eb] p-3">
            <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#7c7277]"><Users className="h-4 w-4" /> Perawat aktif:</span>
            {people.map((p) => (
              <button
                key={p}
                onClick={() => { setActivePerson(p); toast(`${p} sekarang merawat Mochi`); }}
                data-testid={`caretaker-${p}`}
                className={`rounded-full px-3.5 py-1.5 text-[13px] font-bold transition-colors ${
                  state.activePerson === p ? "kk-coral-gradient text-white" : "bg-white text-[#7c7277]"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* summary tiles */}
      <div className="mt-5 grid grid-cols-3 gap-3">
        {[
          { icon: CalendarHeart, val: counts.confirmed, label: "Akan datang" },
          { icon: PartyPopper, val: counts.completed, label: "Kencan selesai" },
          { icon: Heart, val: savedDests.length, label: "Disimpan" },
        ].map((s, i) => (
          <div key={i} className="flex items-center gap-3 rounded-2xl border border-[#efeae4] bg-white p-4">
            <span className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-[#fff0ea] text-[#ff6b4a]"><s.icon className="h-5 w-5" /></span>
            <div>
              <div className="text-2xl font-extrabold leading-none">{s.val}</div>
              <div className="text-[12px] text-[#a89f98]">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* booking tabs */}
      <div className="mt-8 flex gap-6 border-b border-[#efeae4]">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`-mb-px border-b-2 pb-3 text-[14px] font-bold transition-colors ${
              tab === t.id ? "border-[#ff5a5f] text-[#ff5a5f]" : "border-transparent text-[#a89f98]"
            }`}
          >
            {t.label} <span className="text-[12px]">({counts[t.id]})</span>
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {filtered.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-[#e5d9ce] bg-[#fdfbf7] px-6 py-14 text-center">
            <p className="text-[15px] font-bold">Belum ada kencan di sini</p>
            <p className="mt-1 text-[13px] text-[#8a8278]">Yuk cari tempat kencan baru di Jelajah.</p>
            <button onClick={() => navigate("/")} data-testid="empty-explore"
              className="mx-auto mt-4 rounded-full kk-coral-gradient px-5 py-2.5 text-[13px] font-bold text-white">
              Mulai jelajah
            </button>
          </div>
        ) : (
          filtered.map((b) => (
            <div key={b.id} className="flex flex-col gap-4 rounded-3xl border border-[#efeae4] bg-white p-4 sm:flex-row sm:items-center" data-testid={`booking-${b.id}`}>
              <img src={b.memory?.photo || b.img} alt="" className="h-32 w-full rounded-2xl object-cover sm:h-24 sm:w-32" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                    b.status === "completed" ? "bg-[#e6f0e8] text-[#4e8a5f]" :
                    b.status === "cancelled" ? "bg-[#f2eae6] text-[#ad8985]" : "bg-[#eef1e8] text-[#788764]"
                  }`}>
                    {b.status === "completed" ? "Selesai" : b.status === "cancelled" ? "Dibatalkan" : "Terkonfirmasi"}
                  </span>
                  <span className="text-[11px] text-[#b0a29e]">{b.id}</span>
                </div>
                <h3 className="mt-1.5 text-[17px] font-bold">{b.destName}</h3>
                <div className="mt-0.5 flex items-center gap-1 text-[12px] text-[#8a8278]"><MapPin className="h-3.5 w-3.5 text-[#c9878f]" />{b.area}, {b.city}</div>
                <div className="mt-1 text-[13px] text-[#575048]">{b.date} · {b.time} · {b.guests} orang · <b>{rupiah(b.total)}</b>
                  {b.status === "completed" && b.pointsAwarded ? <span className="ml-1 font-bold text-[#4e8a5f]">(+{b.pointsAwarded} poin)</span> : null}
                </div>
                {b.memory?.note && (
                  <div className="mt-2 rounded-xl bg-[#f7f2ec] p-2.5 text-[12px] text-[#7a6a5f]">
                    <span className="text-[#e0a94b]">{"★".repeat(b.memory.rating || 0)}</span>
                    <span className="ml-1">{b.memory.note}</span>
                  </div>
                )}
              </div>
              {b.status === "confirmed" && (
                <div className="flex gap-2 sm:flex-col">
                  <button onClick={() => complete(b.id)} data-testid={`complete-${b.id}`}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-full kk-coral-gradient px-4 py-2.5 text-[12px] font-bold text-white">
                    <CheckCircle2 className="h-4 w-4" /> Simulasikan selesai
                  </button>
                  <button onClick={() => cancel(b.id)} data-testid={`cancel-${b.id}`}
                    className="flex items-center justify-center gap-1.5 rounded-full border border-[#efeae4] px-4 py-2.5 text-[12px] font-bold text-[#a08885]">
                    <XCircle className="h-4 w-4" /> Batal
                  </button>
                </div>
              )}
              {b.status === "completed" && (
                <button onClick={() => openMemory(b)} data-testid={`memory-${b.id}`}
                  className="flex items-center justify-center gap-1.5 rounded-full border border-[#efeae4] bg-white px-4 py-2.5 text-[12px] font-bold text-[#c9566b] sm:flex-col sm:gap-0.5">
                  <Camera className="h-4 w-4" /> {b.memory?.note ? "Edit kenangan" : "Tambah kenangan"}
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* saved */}
      {savedDests.length > 0 && (
        <>
          <h2 className="mb-4 mt-8 flex items-center gap-2 text-2xl font-extrabold tracking-tight">
            <Heart className="h-5 w-5 fill-[#ff5a5f] text-[#ff5a5f]" /> Disimpan
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {savedDests.map((d) => (
              <button key={d.id} onClick={() => navigate(`/destinasi/${d.id}`)} data-testid={`saved-${d.id}`}
                className="group overflow-hidden rounded-2xl border border-[#efeae4] bg-white text-left">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={d.img} alt="" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                  <span onClick={(e) => { e.stopPropagation(); toggleSaved(d.id); toast("Dihapus dari simpanan"); }}
                    className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-white/95 text-[#ff5a5f]">
                    <Heart className="h-4 w-4 fill-[#ff5a5f]" />
                  </span>
                </div>
                <div className="p-2.5">
                  <div className="truncate text-[13px] font-bold">{d.name}</div>
                  <div className="truncate text-[11px] text-[#a89f98]">{d.area} · {rupiah(d.price)}</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Album kenangan */}
      {memories.length > 0 && (
        <>
          <div className="mb-4 mt-8 flex items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight">
              <Images className="h-6 w-6 text-[#ff6b4a]" /> Album kenangan
            </h2>
            <button onClick={shareAlbum} data-testid="share-album"
              className="flex flex-none items-center gap-1.5 rounded-full kk-coral-gradient px-4 py-2 text-[13px] font-bold text-white transition-transform active:scale-95">
              <Share2 className="h-4 w-4" /> Bagikan album
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {memories.map((b) => (
              <div key={b.id} className="group relative overflow-hidden rounded-2xl border border-[#efeae4] bg-white kk-shadow-sm" data-testid={`album-${b.id}`}>
                <img src={b.memory.photo || b.img} alt={b.destName} className="aspect-square w-full object-cover transition-transform group-hover:scale-105" />
                <div className="absolute left-2 top-2 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-bold text-[#c9566b] backdrop-blur">
                  {state.coupleName}
                </div>
                <button onClick={() => setCardFor(b)} data-testid={`download-card-${b.id}`}
                  className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-white/90 text-[#ff5a5f] backdrop-blur transition-opacity active:scale-95"
                  title="Buat kartu kenangan">
                  <Download className="h-4 w-4" />
                </button>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-2.5 text-white">
                  <div className="text-[11px] text-[#ffd36b]">{"★".repeat(b.memory.rating || 0)}</div>
                  <div className="truncate text-[12px] font-bold">{b.destName}</div>
                  {b.memory.note && <div className="line-clamp-2 text-[10px] text-white/85">{b.memory.note}</div>}
                  <div className="mt-1 flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-white/70">
                    <CalendarHeart className="h-2.5 w-2.5" />
                    {new Date(b.date).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Mochi progress */}
      <div className="mt-8 flex items-center gap-4 rounded-3xl border border-[#efeae4] bg-white p-5">
        <span className="grid h-14 w-14 flex-none place-items-center rounded-2xl bg-[#fff0ea] text-[#ff6b4a]"><Cat className="h-7 w-7" /></span>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#ff5a5f]"><Sparkles className="h-3.5 w-3.5" /> Progress Mochi</div>
          <div className="mt-0.5 text-[16px] font-bold">Level {state.mochi.level} · {state.mochi.equipped ? "aksesori aktif" : "belum pakai aksesori"}</div>
          <p className="text-[13px] text-[#8a8278]">Rawat Mochi & selesaikan kencan untuk naik level bersama.</p>
        </div>
        <button onClick={() => navigate("/mochi")} data-testid="go-mochi"
          className="hidden rounded-full kk-coral-gradient px-4 py-2.5 text-[13px] font-bold text-white sm:block">Kunjungi Mochi</button>
      </div>

      {/* Lencana musim */}
      <div className="mt-5 rounded-3xl border border-[#efeae4] bg-white p-5">
        <h2 className="flex items-center gap-2 text-[16px] font-bold"><Award className="h-5 w-5 text-[#e0a94b]" /> Lencana musim</h2>
        <p className="mt-0.5 text-[12px] text-[#8a8278]">Rawat Mochi di musim berbeda sepanjang tahun untuk koleksi lengkap.</p>
        <div className="mt-3 grid grid-cols-4 gap-2">
          {SEASON_ORDER.map((id) => {
            const s = SEASONS[id];
            const earned = (state.seasonBadges || []).includes(id);
            return (
              <div key={id} data-testid={`season-badge-${id}`}
                className={`rounded-2xl border p-3 text-center transition-all ${earned ? "bg-[#fffaf0]" : "opacity-50 grayscale"}`}
                style={earned ? { borderColor: s.badge } : {}}>
                <div className="text-2xl leading-none">{s.emoji}</div>
                <div className="mt-1 text-[11px] font-bold leading-tight text-[#6e594c]">{s.label}</div>
                <div className="text-[10px] font-semibold" style={{ color: earned ? s.badge : "#b0a29e" }}>{earned ? "Terkumpul" : "Terkunci"}</div>
              </div>
            );
          })}
        </div>
      </div>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Ubah profil pasangan">
        <div className="space-y-3 p-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-[12px] font-semibold text-[#8f7c7c]">Nama pertama</label>
              <input value={form.personA} onChange={(e) => setForm((f) => ({ ...f, personA: e.target.value }))}
                data-testid="edit-persona" className="w-full rounded-xl border border-[#e8dfd7] bg-white px-3 py-2.5 text-[14px] outline-none focus:border-[#ff5a5f]" />
            </div>
            <div>
              <label className="mb-1.5 block text-[12px] font-semibold text-[#8f7c7c]">Nama kedua</label>
              <input value={form.personB} onChange={(e) => setForm((f) => ({ ...f, personB: e.target.value }))}
                data-testid="edit-personb" className="w-full rounded-xl border border-[#e8dfd7] bg-white px-3 py-2.5 text-[14px] outline-none focus:border-[#ff5a5f]" />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-[12px] font-semibold text-[#8f7c7c]">Tanggal jadian</label>
            <input type="date" value={form.anniversary} onChange={(e) => setForm((f) => ({ ...f, anniversary: e.target.value }))}
              data-testid="edit-anniversary" className="w-full rounded-xl border border-[#e8dfd7] bg-white px-3 py-2.5 text-[14px] outline-none focus:border-[#ff5a5f]" />
          </div>
          <button onClick={saveEdit} data-testid="save-profile"
            className="mt-2 w-full rounded-full kk-coral-gradient py-3 text-[15px] font-bold text-white">Simpan</button>
        </div>
      </Modal>

      <Modal open={!!memoryFor} onClose={() => setMemoryFor(null)} title="Kenangan kencan">
        <div className="space-y-4 p-5">
          <div>
            <label className="mb-1.5 block text-[12px] font-semibold text-[#8f7c7c]">Seberapa berkesan?</label>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setMem((m) => ({ ...m, rating: n }))} data-testid={`mem-star-${n}`}>
                  <Star className={`h-7 w-7 ${n <= mem.rating ? "fill-[#f59e0b] text-[#f59e0b]" : "text-[#dcd3ca]"}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-[12px] font-semibold text-[#8f7c7c]">Catatan kalian</label>
            <textarea value={mem.note} onChange={(e) => setMem((m) => ({ ...m, note: e.target.value }))}
              rows={3} placeholder="Cerita singkat momen kencan kalian…" data-testid="mem-note"
              className="w-full rounded-xl border border-[#e8dfd7] bg-white px-3 py-2.5 text-[14px] outline-none focus:border-[#ff5a5f]" />
          </div>
          <div>
            <label className="mb-1.5 block text-[12px] font-semibold text-[#8f7c7c]">Pilih foto kenangan</label>
            <div className="flex gap-2">
              {(() => {
                const mb = bookings.find((b) => b.id === memoryFor);
                const dst = mb ? getDestination(mb.destId) : null;
                const opts = dst ? dst.gallery : [];
                return opts.map((g, i) => (
                  <button key={i} onClick={() => setMem((m) => ({ ...m, photo: g }))} data-testid={`mem-photo-${i}`}
                    className={`h-16 w-20 overflow-hidden rounded-xl border-2 ${mem.photo === g ? "border-[#ff5a5f]" : "border-transparent"}`}>
                    <img src={g} alt="" className="h-full w-full object-cover" />
                  </button>
                ));
              })()}
            </div>
            <input value={mem.photo?.startsWith("http") || mem.photo?.startsWith("/") ? "" : mem.photo || ""}
              onChange={(e) => setMem((m) => ({ ...m, photo: e.target.value }))}
              placeholder="atau tempel URL foto (opsional)" data-testid="mem-photo-url"
              className="mt-2 w-full rounded-xl border border-[#e8dfd7] bg-white px-3 py-2.5 text-[13px] outline-none focus:border-[#ff5a5f]" />
          </div>
          <button onClick={saveMemory} data-testid="save-memory"
            className="w-full rounded-full kk-coral-gradient py-3 text-[15px] font-bold text-white">Simpan kenangan</button>
        </div>
      </Modal>

      <Modal open={!!cardFor} onClose={() => setCardFor(null)} title="Kartu kenangan">
        <div className="space-y-4 p-5">
          <p className="text-center text-[13px] text-[#8a8278]">Unduh kartu cantik ini dan bagikan momen kalian ke media sosial.</p>
          {cardFor && (
            <div className="flex justify-center">
              <div className="kk-shadow-sm rounded-[28px]">
                <MemoryShareCard ref={cardRef} booking={cardFor} coupleName={state.coupleName} />
              </div>
            </div>
          )}
          <button onClick={downloadCard} disabled={saving} data-testid="do-download-card"
            className="flex w-full items-center justify-center gap-2 rounded-full kk-coral-gradient py-3 text-[15px] font-bold text-white disabled:opacity-60">
            <Download className="h-5 w-5" /> {saving ? "Menyiapkan gambar…" : "Unduh gambar"}
          </button>
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => shareCard("whatsapp")} disabled={saving} data-testid="share-whatsapp"
              className="flex items-center justify-center gap-1.5 rounded-full bg-[#25D366] py-2.5 text-[13px] font-bold text-white transition-transform active:scale-95 disabled:opacity-60">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </button>
            <button onClick={() => shareCard("instagram")} disabled={saving} data-testid="share-instagram"
              className="flex items-center justify-center gap-1.5 rounded-full py-2.5 text-[13px] font-bold text-white transition-transform active:scale-95 disabled:opacity-60"
              style={{ background: "linear-gradient(135deg,#f58529,#dd2a7b,#8134af)" }}>
              <Instagram className="h-4 w-4" /> Story
            </button>
            <button onClick={() => shareCard("more")} disabled={saving} data-testid="share-more"
              className="flex items-center justify-center gap-1.5 rounded-full border border-[#efeae4] bg-white py-2.5 text-[13px] font-bold text-[#7c7277] transition-transform active:scale-95 disabled:opacity-60">
              <Share2 className="h-4 w-4" /> Bagikan
            </button>
          </div>
          <p className="text-center text-[11px] text-[#a89f98]">Di ponsel, tombol ini langsung membuka pilihan bagikan ke aplikasi.</p>
        </div>
      </Modal>
    </div>
  );
}
